import React, { useMemo, useState, useRef } from "react";
import { Button, CircularProgress, IconButton } from "@mui/material";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import FileUploadRoundedIcon from "@mui/icons-material/FileUploadRounded";

import { usePutApprovedApplicationDocumentsMutation } from "apis/ApprovedApplicationDocument";

import { getImageUrl } from "utils";
import getFileExtension from "utils/getFileExtension";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import downloadFile from "utils/downloadFile";

import Backdrop from "Components/Common/Backdrop";
import { estimateIssued } from "common/constant";
import Tooltip from "Components/Common/Tooltip";

const MAX_FILE_SIZE = 5 * 1024 * 1024;

const DownloadUploadActionButton = ({
  item: {
    applicationFormUrl,
    id,
    applicationStatus,
    applicationType,
    approvedApplicationDocumentURL,
  },
  t,
}) => {
  const documentUploadRef = useRef();

  const [uploadDocument, { isLoading }] =
    usePutApprovedApplicationDocumentsMutation();

  const [documentToPreview, setDocumentToPreview] = useState();
  const [uploadedFile, setUploadedFile] = useState();

  const isEstimationIssued = useMemo(
    () => applicationStatus?.name === estimateIssued,
    [applicationStatus]
  );

  const handleUploadFile = async (event) => {
    try {
      const fileExtension = getFileExtension(
        event.target.files[0]?.name
      ).toLowerCase();
      const fileSize = event.target.files[0]?.size;
      if (fileSize >= MAX_FILE_SIZE) {
        toast.error("File size should be less than 5MB");
      } else if (fileExtension !== "pdf") {
        toast.error("Invalid file format, Please upload pdf file");
      } else {
        setDocumentToPreview({
          url: URL.createObjectURL(event.target.files[0]),
          documentTypeName: "Application Form",
          documentType: "pdf",
          documentName: "applicationForm",
        });
        setUploadedFile(event.target.files[0]);
      }
      event.target.value = "";
    } catch (err) {}
  };

  const uploadDocumentBtnHandler = async () => {
    try {
      const formData = new FormData();
      formData.append("documentURL", uploadedFile);
      await uploadDocument(formData, id);
      handleClose();
    } catch (error) {}
  };
  const isDownloadIconDisable = applicationStatus?.id <= 3;

  const isUploadIconDisable = !(
    applicationStatus?.name?.toLowerCase() === "approved" &&
    !approvedApplicationDocumentURL
  );

  const handleClose = () => {
    setDocumentToPreview(null);
    setUploadedFile(null);
    documentUploadRef.current.value = "";
  };

  return (
    <>
      <Backdrop open={isLoading} />
      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={handleClose}
          isDownloadable={false}
          actionButton={
            <Button
              onClick={uploadDocumentBtnHandler}
              color="primary"
              className="mb-1 me-1"
              variant="contained"
              disabled={isLoading}
            >
              {t("Upload")} {isLoading && <CircularProgress size={20} />}
            </Button>
          }
          {...documentToPreview}
        />
      )}

      <IconButton
        disabled={isDownloadIconDisable || !applicationFormUrl}
        onClick={() => {
          downloadFile(
            getImageUrl(applicationFormUrl),
            `${applicationType?.name}.pdf`
          );
        }}
      >
        <Tooltip title={t("Download Application Form")}>
          <FileDownloadRoundedIcon
            color={
              isDownloadIconDisable || !applicationFormUrl
                ? "disabled"
                : "primary"
            }
            className="cursor-pointer mt-1"
            fontSize="medium"
          />
        </Tooltip>
      </IconButton>

      {!isEstimationIssued && (
        <IconButton disabled={isUploadIconDisable} component="label">
          <Tooltip title={t("Upload Signed Application Form")}>
            <div>
              <FileUploadRoundedIcon
                sx={{
                  color: isUploadIconDisable ? "disabled" : "#299923",
                }}
                fontSize="medium"
              />

              <input
                style={{
                  display: "none",
                }}
                ref={documentUploadRef}
                accept=".pdf"
                type="file"
                onChange={(e) => handleUploadFile(e)}
              />
            </div>
          </Tooltip>
        </IconButton>
      )}
    </>
  );
};

DownloadUploadActionButton.propTypes = {
  item: PropTypes.object,
  t: PropTypes.func,
};

export default withTranslation()(DownloadUploadActionButton);
